import * as Yup from 'yup';
import { bondInsuranceRequiresComplement } from '../../utils/bondInsurancePropertyComplement';
import { EmploymentRelationshipEnum } from '../../enums/employment-relationship.enum';
import { PersonTypesEnum } from '../../enums/person-types.enum';
import { PoliticallyExposedPersonEnum } from '../../enums/politically-exposed-person.enum';
import { unmaskCpfOrCnpj } from '../../services/masks';
import { validateCNPJ, validateCPF } from '../../services/validators';
// Importa o método customizado de validação de email sem acentos
import '../fire-insurance-budget-form/validators/constants';

const propertyRequiredField = (label: string) => `O campo ${label} é obrigatório.`;
const tenantRequiredField = (label: string) => `O campo ${label} é obrigatório.`;
const tenantDateField = (label: string) =>
  Yup.date()
    .nullable()
    .transform((value, originalValue) => (originalValue === '' ? null : value))
    .typeError(`O campo ${label} deve ser uma data válida.`)
    .required(tenantRequiredField(label));

export const legalCommercialResidentialSchema = Yup.object().shape({
  tenant_info: Yup.object().shape({
    company: Yup.string().required(tenantRequiredField('Empresa')),
    document: Yup.string().isValidCnpj().required(tenantRequiredField('CNPJ')),
    foundation_date: tenantDateField('Data de constituição'),
    last_contractual_alteration_date: tenantDateField('Última alteração contratual'),
    share_capital: Yup.string().required(tenantRequiredField('Capital social')),
    paid_in_capital: Yup.string().required(tenantRequiredField('Capital integralizado')),
    anual_revenue: Yup.string().required(tenantRequiredField('Faturamento anual')),
    phone: Yup.string().required(tenantRequiredField('Telefone')),
    contact_name: Yup.string().required(tenantRequiredField('Nome do contato')),
    contact_phone: Yup.string().required(tenantRequiredField('Celular do contato')),
    zip_code: Yup.string()
      .min(9, 'Número do CEP Inválido')
      .max(9, 'Número do CEP Inválido')
      .required(tenantRequiredField('CEP')),
    street: Yup.string().required(tenantRequiredField('Logradouro')),
    district: Yup.string().required(tenantRequiredField('Bairro')),
    city: Yup.string().required(tenantRequiredField('Cidade')),
    state: Yup.string().required(tenantRequiredField('Estado')),
    number: Yup.string().required(tenantRequiredField('Número')),
    property_type: Yup.string().required(tenantRequiredField('Tipo do imóvel')),
    email: Yup.string().isValidEmailWithoutAccents().required(tenantRequiredField('E-mail')),
    partners: Yup.array().of(
      Yup.object().shape({
        name: Yup.string().required(tenantRequiredField('Nome do sócio')),
        document: Yup.string()
          .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
          .required(tenantRequiredField('CPF do sócio')),
        percent: Yup.number().required(tenantRequiredField('% do capital social')),
        date_of_entry: tenantDateField('Data de entrada')
      })
    )
  })
});

export const naturalResidentialSchema = (invalidEmail: string) =>
  Yup.object().shape({
    // (invalidEmail: string) =>
    tenant_info: Yup.object().shape({
      main_tenant_name: Yup.string().required(tenantRequiredField('Nome do locatário')),
      main_tenant_cpf: Yup.string()
        .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
        .required(tenantRequiredField('CPF')),
      // main_tenant_rg: Yup.string().required(requiredField),
      // main_tenant_rg_expeditor_organ: Yup.string().required(requiredField),
      // main_tenant_rg_emission_date: Yup.date().required(requiredField),
      main_tenant_email: Yup.string()
        .isValidEmailWithoutAccents()
        .notOneOf(
          [invalidEmail],
          'O email do locatário não pode ser igual ao email da Imobiliária!'
        )
        .required(tenantRequiredField('E-mail')),
      main_tenant_phone: Yup.string()
        .required(tenantRequiredField('Celular'))
        .isValidMaskedTelephone(),
      main_tenant_gender: Yup.string().required(tenantRequiredField('Gênero')),
      main_tenant_birth_date: tenantDateField('Data de nascimento'),
      main_tenant_mother_name: Yup.string().required(tenantRequiredField('Nome da mãe')),
      main_tenant_marital_status: Yup.string().required(tenantRequiredField('Estado civil')),
      partner_rent_composition: Yup.boolean().when('main_tenant_marital_status', {
        is: 'CASADO',
        then: Yup.boolean().required(tenantRequiredField('Composição de renda do cônjuge'))
      }),
      partner_name: Yup.string().when('partner_rent_composition', {
        is: true,
        then: Yup.string().required(tenantRequiredField('Nome do cônjuge'))
      }),
      partner_cpf: Yup.string().when('partner_rent_composition', {
        is: true,
        then: Yup.string()
          .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
          .required(tenantRequiredField('CPF do cônjuge'))
      }),
      employment_relationship: Yup.string().required(tenantRequiredField('Vínculo empregatício')),
      main_tenant_profession: Yup.string().required(tenantRequiredField('Profissão')),
      main_tenant_politically_exposed_person: Yup.string().required(
        tenantRequiredField('Pessoa politicamente exposta')
      ),
      main_tenant_close_to_politically_exposed_person: Yup.object().when(
        'main_tenant_politically_exposed_person',
        {
          is: PoliticallyExposedPersonEnum.closeTo,
          then: Yup.object().shape({
            politically_exposed_person_name: Yup.string().required(
              tenantRequiredField('Nome da pessoa politicamente exposta')
            ),
            politically_exposed_person_cpf: Yup.string().required(
              tenantRequiredField('CPF da pessoa politicamente exposta')
            ),
            degree_of_kinship_to_politically_exposed_person: Yup.string().required(
              tenantRequiredField('Grau de parentesco')
            )
          }),
          otherwise: Yup.object()
        }
      ),
      employment_admission_date: Yup.date().when('employment_relationship', {
        is:
          EmploymentRelationshipEnum.privateServant ||
          EmploymentRelationshipEnum.civilServant,
        then: tenantDateField('Data de admissão')
      }),
      resident: Yup.boolean().required(tenantRequiredField('Residente no imóvel')),
      resident_name: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Nome do residente'))
      }),
      resident_cpf: Yup.string().when('resident', {
        is: false,
        then: Yup.string()
          .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
          .required(tenantRequiredField('CPF do residente'))
      }),
      resident_birth_date: Yup.date().when('resident', {
        is: false,
        then: tenantDateField('Data de nascimento do residente')
      }),
      // resident_rg: Yup.string()
      //   .when('resident', {
      //     is: false,
      //     then: Yup.string()
      //       .required(requiredField)
      //   }),
      // resident_rg_expeditor_organ: Yup.string()
      //   .when('resident', {
      //     is: false,
      //     then: Yup.string()
      //       .required(requiredField)
      //   }),
      // resident_rg_emission_date: Yup.string()
      //   .when('resident', {
      //     is: false,
      //     then: Yup.string()
      //       .required(requiredField)
      //   }),
      resident_phone: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Celular do residente'))
      }),
      resident_gender: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Gênero do residente'))
      }),
      resident_nationality: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Nacionalidade do residente'))
      }),
      // resident_current_address_zipcode: Yup.string()
      //   .when('resident', {
      //     is: false,
      //     then: Yup.string()
      //       .min(9, 'Número do CEP Inválido')
      //       .max(9, 'Número do CEP Inválido')
      //       .required(requiredField),
      //   }),
      resident_politically_exposed_person: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(
          tenantRequiredField('Pessoa politicamente exposta do residente')
        )
      }),
      resident_close_to_politically_exposed_person: Yup.object().when('resident', {
        is: false,
        then: Yup.object().shape({
          politically_exposed_person_name: Yup.string().when(
            'politically_exposed_person',
            {
              is: 'CLOSE_TO',
              then: Yup.string().required(tenantRequiredField('Nome da pessoa politicamente exposta')),
              otherwise: Yup.string()
            }
          ),
          politically_exposed_person_cpf: Yup.string().when(
            'politically_exposed_person',
            {
              is: 'CLOSE_TO',
              then: Yup.string().required(tenantRequiredField('CPF da pessoa politicamente exposta')),
              otherwise: Yup.string()
            }
          ),
          degree_of_kinship_to_politically_exposed_person: Yup.string().when(
            'politically_exposed_person',
            {
              is: 'CLOSE_TO',
              then: Yup.string().required(tenantRequiredField('Grau de parentesco')),
              otherwise: Yup.string()
            }
          )
        })
      }),
      resident_profession: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Profissão do residente'))
      }),
      // resident_monthly_fixed_income: Yup.string()
      //   .when('resident', {
      //     is: false,
      //     then: Yup.string()
      //       .required(requiredField)
      //   }),
      resident_employment_relationship: Yup.string().when('resident', {
        is: false,
        then: Yup.string().required(tenantRequiredField('Vínculo empregatício do residente'))
      }),
      resident_email: Yup.string().when('resident', {
        is: false,
        then: Yup.string()
          .isValidEmailWithoutAccents()
          .required(tenantRequiredField('E-mail do residente'))
      }),
      quiz_company_created: Yup.boolean().required(tenantRequiredField('Empresa já constituída')),
      quiz_company_cnpj: Yup.string().when('quiz_company_created', {
        is: true,
        then: Yup.string().isValidCnpj().required(tenantRequiredField('CNPJ da empresa'))
      }),
      quiz_company_rent: Yup.string().when('quiz_company_created', {
        is: true,
        then: Yup.string().required(tenantRequiredField('Renda mensal da empresa'))
      }),
      quiz_partners: Yup.boolean().required(tenantRequiredField('Possui sócios')),
      quiz_company_field_of_activity: Yup.string().when('quiz_company_created', {
        is: true,
        then: Yup.string().required(tenantRequiredField('Ramo de atividade'))
      }),
      partners: Yup.array().when('quiz_partners', {
        is: true,
        then: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required(tenantRequiredField('Nome do sócio')),
            document: Yup.string()
              .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
              .required(tenantRequiredField('CPF do sócio'))
          })
        )
      }),
      include_sympathise_tenants: Yup.boolean().required(
        tenantRequiredField('Adicionar locatário solidário')
      ),
      sympathise_tenants: Yup.array().when('include_sympathise_tenants', {
        is: true,
        then: Yup.array().of(
          Yup.object().shape({
            name: Yup.string().required(tenantRequiredField('Nome do locatário solidário')),
            cpf: Yup.string()
              .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
              .required(tenantRequiredField('CPF do locatário solidário')),
            // rg: Yup.string().required(requiredField),
            // rg_expeditor_organ: Yup.string().required(requiredField),
            // rg_emission_date: Yup.string().required(requiredField),
            phone: Yup.string().required(tenantRequiredField('Telefone do locatário solidário')),
            gender: Yup.string().required(tenantRequiredField('Gênero do locatário solidário')),
            birth_date: tenantDateField('Data de nascimento do locatário solidário'),
            mother_name: Yup.string().required(
              tenantRequiredField('Nome da mãe do locatário solidário')
            ),
            marital_status: Yup.string().required(
              tenantRequiredField('Estado civil do locatário solidário')
            ),
            nationality: Yup.string().required(
              tenantRequiredField('Nacionalidade do locatário solidário')
            ),
            // current_address_zipcode: Yup.string().min(9, 'Número do CEP Inválido').max(9, 'Número do CEP Inválido').required(requiredField),
            politically_exposed_person: Yup.string().required(
              tenantRequiredField('Pessoa politicamente exposta do locatário solidário')
            ),
            close_to_politically_exposed_person: Yup.object().when(
              'politically_exposed_person',
              {
                is: PoliticallyExposedPersonEnum.closeTo,
                then: Yup.object().shape({
                  politically_exposed_person_name: Yup.string().required(
                    tenantRequiredField('Nome da pessoa politicamente exposta')
                  ),
                  politically_exposed_person_cpf: Yup.string().required(
                    tenantRequiredField('CPF da pessoa politicamente exposta')
                  ),
                  degree_of_kinship_to_politically_exposed_person: Yup.string().required(
                    tenantRequiredField('Grau de parentesco')
                  )
                }),
                otherwise: Yup.object()
              }
            ),
            profession: Yup.string().required(
              tenantRequiredField('Profissão do locatário solidário')
            ),
            monthly_fixed_income: Yup.string().required(
              tenantRequiredField('Renda mensal do locatário solidário')
            ),
            resident: Yup.string().required(
              tenantRequiredField('Residente do locatário solidário')
            ),
            employment_relationship: Yup.string().required(
              tenantRequiredField('Vínculo empregatício do locatário solidário')
            ),
            email: Yup.string()
              .isValidEmailWithoutAccents()
              .required(tenantRequiredField('E-mail do locatário solidário'))
          })
        )
      })
    })
  });

export const validationSchemas = [
  Yup.object().shape({
    initial_info: Yup.object().shape({
      // A imobiliária é armazenada como objeto, por isso não pode ser validada
      // como string.
      estate: Yup.mixed().required('O campo Imobiliária é obrigatório.'),
      purpose: Yup.string().required('O campo Tipo do imóvel é obrigatório.'),
      tenant_type: Yup.string().required('O campo Tipo do locatário é obrigatório.'),
      analysis_assurance: Yup.string().required('O campo Plano de análise é obrigatório.')
    })
  }),
  Yup.object().shape({
    property_info: Yup.object().shape({
      // rent_insurance_contract_start_date: Yup.date().required(requiredField),
      // rent_insurance_contract_end_date: Yup.date().required(requiredField),
      // rental_period_start_date: Yup.date().required(requiredField),
      // rental_period_end_date: Yup.date().required(requiredField),
      zip_code: Yup.string()
        .min(9, 'Número do CEP Inválido')
        .max(9, 'Número do CEP Inválido')
        .required(propertyRequiredField('CEP')),
      street: Yup.string().required(propertyRequiredField('Logradouro')),
      district: Yup.string().required(propertyRequiredField('Bairro')),
      city: Yup.string().required(propertyRequiredField('Cidade')),
      state: Yup.string().required(propertyRequiredField('UF')),
      number: Yup.number()
        .required(propertyRequiredField('Número'))
        .typeError('Deve ser um valor numérico'),
      property_type: Yup.string().required(propertyRequiredField('Tipo do imóvel')),
      complement: Yup.string().when('property_type', {
        is: (value: string) => bondInsuranceRequiresComplement(value),
        then: Yup.string().required(propertyRequiredField('Complemento'))
      }),
      reason: Yup.string().required(propertyRequiredField('Motivo da locação')),
      rent_value: Yup.string()
        .greaterThanZero('O valor do aluguel deve ser maior que R$ 0,00')
        .required(propertyRequiredField('Valor do aluguel')),

      // To toggle if it's optional or not
      location_in_progress: Yup.boolean(),
      rental_period_start_date: Yup.date().when('location_in_progress', {
        is: true,
        then: Yup.date().required(propertyRequiredField('Data inicial do período de locação'))
      }),
      rental_period_end_date: Yup.date().when('location_in_progress', {
        is: true,
        then: Yup.date().required(propertyRequiredField('Data final do período de locação'))
      }),
      is_condominium_fee_required: Yup.boolean(),
      condominium_fee: Yup.string().when('is_condominium_fee_required', {
        is: true,
        then: Yup.string().required(propertyRequiredField('Valor do condomínio'))
      }),
      is_energy_bill_required: Yup.boolean(),
      energy_bill: Yup.string().when('is_energy_bill_required', {
        is: true,
        then: Yup.string().required(propertyRequiredField('Valor da energia'))
      }),
      is_gas_bill_required: Yup.boolean(),
      gas_bill: Yup.string().when('is_gas_bill_required', {
        is: true,
        then: Yup.string().required(propertyRequiredField('Valor do gás canalizado'))
      }),
      is_water_bill_required: Yup.boolean(),
      water_bill: Yup.string().when('is_water_bill_required', {
        is: true,
        then: Yup.string().required(propertyRequiredField('Valor da água'))
      }),
      is_taxes_value_required: Yup.boolean(),
      taxes_value: Yup.string().when('is_taxes_value_required', {
        is: true,
        then: Yup.string().required(propertyRequiredField('Valor do IPTU mensal'))
      }),
      // rent_indemnity_period: Yup.number().required(requiredField),
      validity: Yup.number()
        .required(propertyRequiredField('Vigência do seguro fiança'))
        .typeError('Você deve selecionar um valor válido'),
      total_amount: Yup.number().required(propertyRequiredField('Valor total')),
      real_occupant_name: Yup.string().when('reason', {
        is: 'Locação para moradia',
        then: Yup.string().required(propertyRequiredField('Nome do real ocupante'))
      }),
      real_occupant_doc: Yup.string().when('reason', {
        is: 'Locação para moradia',
        then: Yup.string()
          .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'Documento inválido!')
          .required(propertyRequiredField('CPF do real ocupante'))
      })
    })
  }),
  // Yup.object().shape({
  //   owner_info: Yup.object().shape({
  //     name: Yup.string().required(requiredField),
  //     document: Yup.string().required(requiredField),
  //     email: Yup.string().required(requiredField),
  //     phone: Yup.string().required(requiredField),
  //     zip_code: Yup.string()
  //       .min(9, 'Número do CEP Inválido')
  //       .max(9, 'Número do CEP Inválido')
  //       .required(requiredField),
  //     street: Yup.string().required(requiredField),
  //     district: Yup.string().required(requiredField),
  //     city: Yup.string().required(requiredField),
  //     state: Yup.string().required(requiredField),
  //     number: Yup.number().required(requiredField).typeError('Deve ser um valor numérico')
  //   })
  // }),
  Yup.object().shape({
    tenant_info: Yup.object().shape({
      company: Yup.string().required(tenantRequiredField('Empresa')),
      document: Yup.string().isValidCnpj().required(tenantRequiredField('CNPJ')),
      foundation_date: tenantDateField('Data de constituição'),
      last_contractual_alteration_date: tenantDateField('Última alteração contratual'),
      share_capital: Yup.string().required(tenantRequiredField('Capital social')),
      paid_in_capital: Yup.string().required(tenantRequiredField('Capital integralizado')),
      anual_revenue: Yup.string().required(tenantRequiredField('Receita anual')),
      phone: Yup.string().required(tenantRequiredField('Telefone')),
      contact_name: Yup.string().required(tenantRequiredField('Nome do contato')),
      contact_phone: Yup.string().required(tenantRequiredField('Telefone do contato')),
      zip_code: Yup.string()
        .min(9, 'Número do CEP Inválido')
        .max(9, 'Número do CEP Inválido')
        .required(tenantRequiredField('CEP')),
      street: Yup.string().required(tenantRequiredField('Logradouro')),
      district: Yup.string().required(tenantRequiredField('Bairro')),
      city: Yup.string().required(tenantRequiredField('Cidade')),
      state: Yup.string().required(tenantRequiredField('UF')),
      number: Yup.string().required(tenantRequiredField('Número')),
      complement: Yup.string().required(tenantRequiredField('Complemento')),
      email: Yup.string()
        .isValidEmailWithoutAccents()
        .required(tenantRequiredField('E-mail')),
      partners: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required(tenantRequiredField('Nome do sócio')),
          document: Yup.string()
            .matches(/^\d{3}\.\d{3}\.\d{3}\-\d{2}$/, 'CPF inválido!')
            .required(tenantRequiredField('CPF do sócio')),
          percent: Yup.number().required(tenantRequiredField('% do capital social')),
          date_of_entry: tenantDateField('Data de entrada')
        })
      )
    })
  })
];

/**
 * Valida os campos do primeiro passo que não pertencem ao schema de
 * `initial_info` (documento do locatário e seguradoras ativas).
 *
 * Essa validação fica no `validate` do Formik para que os erros não sejam
 * perdidos quando o usuário marca os campos como tocados.
 */
export const validateInitialInfo = (values: any) => {
  const errors: any = {};
  const initialInfo = values?.initial_info || {};
  const tenantInfo = values?.tenant_info || {};

  if (initialInfo.estate && !initialInfo.estate.insurers?.length) {
    errors.initial_info = {
      insurers: 'O campo Seguradoras / Garantidoras ativas é obrigatório.'
    };
  }

  if (initialInfo.tenant_type === PersonTypesEnum.natural) {
    const cpf = unmaskCpfOrCnpj(tenantInfo.main_tenant_cpf || '');

    if (!cpf) {
      errors.tenant_info = {
        ...(errors.tenant_info || {}),
        main_tenant_cpf: 'O campo CPF é obrigatório.'
      };
    } else if (cpf.length !== 11 || !validateCPF(cpf)) {
      errors.tenant_info = {
        ...(errors.tenant_info || {}),
        main_tenant_cpf: 'CPF inválido!'
      };
    }
  }

  if (initialInfo.tenant_type === PersonTypesEnum.legal) {
    const cnpj = unmaskCpfOrCnpj(tenantInfo.document || '');

    if (!cnpj) {
      errors.tenant_info = {
        ...(errors.tenant_info || {}),
        document: 'O campo CNPJ é obrigatório.'
      };
    } else if (cnpj.length !== 14 || !validateCNPJ(cnpj)) {
      errors.tenant_info = {
        ...(errors.tenant_info || {}),
        document: 'CNPJ inválido!'
      };
    }
  }

  return errors;
};
